import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronRight, ChevronLeft, CheckCircle, Building2, Briefcase,
  User, Wrench, Globe, Search, Sparkles, Loader2, UserCheck,
  Target, BookOpen, MessageSquare, Bot, ArrowRight, AlertCircle,
  RotateCcw
} from "lucide-react";
import { invokeFunction } from "../../lib/invokeFunction";

const CLIENT_TYPES = [
  {
    id: "hr_specialist",
    title: "HR-специалист",
    subtitle: "HRD, HRBP, HR-менеджер",
    description: "Ищу экспертизу для развития сотрудников и решения HR-задач компании",
    icon: UserCheck,
    color: "primary"
  },
  {
    id: "business_owner",
    title: "Владелец бизнеса",
    subtitle: "Предприниматель, основатель, CEO",
    description: "Развиваю свой бизнес и ищу экспертную поддержку по стратегическим вопросам",
    icon: Building2,
    color: "accent"
  },
  {
    id: "company_employee",
    title: "Специалист компании",
    subtitle: "Сотрудник, менеджер, руководитель",
    description: "Работаю в компании и хочу получить экспертную консультацию для решения рабочих задач",
    icon: Briefcase,
    color: "blue"
  },
  {
    id: "freelancer",
    title: "Фрилансер",
    subtitle: "Независимый специалист",
    description: "Работаю независимо и ищу экспертизу для развития своих навыков и услуг",
    icon: Wrench,
    color: "green"
  }
];

const colorClasses = {
  primary: {
    bg: "bg-primary-600/20",
    icon: "text-primary-400",
    border: "border-primary-500/40",
    activeBg: "bg-primary-500/10"
  },
  accent: {
    bg: "bg-accent-600/20",
    icon: "text-accent-400",
    border: "border-accent-500/40",
    activeBg: "bg-accent-500/10"
  },
  blue: {
    bg: "bg-blue-600/20",
    icon: "text-blue-400",
    border: "border-blue-500/40",
    activeBg: "bg-blue-500/10"
  },
  green: {
    bg: "bg-green-600/20",
    icon: "text-green-400",
    border: "border-green-500/40",
    activeBg: "bg-green-500/10"
  }
};

export default function ClientOnboardingWizard({ user, onComplete, onSave }) {
  const [step, setStep] = useState(1);
  const [clientType, setClientType] = useState(user?.client_type || "");
  const [jobTitle, setJobTitle] = useState(user?.job_title || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [companyWebsite, setCompanyWebsite] = useState(user?.company_website || "");
  const [companySize, setCompanySize] = useState(user?.company_size || "");
  const [companyDescription, setCompanyDescription] = useState(user?.company_description || "");
  const [industry, setIndustry] = useState(user?.industry || "");
  const [freelanceServices, setFreelanceServices] = useState(
    Array.isArray(user?.freelance_services) && user.freelance_services.length > 0
      ? user.freelance_services
      : [""]
  );

  // AI company lookup state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiCompany, setAiCompany] = useState(null);
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [skippedCompany, setSkippedCompany] = useState(false);

  const totalSteps = clientType === "freelancer" ? 3 : clientType ? 3 : 1;

  const handleSelectType = (type) => {
    setClientType(type);
    setStep(2);
  };

  const handleAnalyzeCompany = async () => {
    if (!companyWebsite.trim()) return;
    setAiLoading(true);
    setAiError("");
    setAiCompany(null);
    setCompanyConfirmed(false);

    try {
      let url = companyWebsite.trim();
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
        setCompanyWebsite(url);
      }

      const { data, error } = await invokeFunction("analyze-company", {
        body: { website: url }
      });

      if (error) throw new Error(error.message || "Ошибка анализа");

      if (data?.company) {
        setAiCompany(data.company);
        if (data.company.company_name) setCompanyName(data.company.company_name);
        if (data.company.industry) setIndustry(data.company.industry);
        if (data.company.company_size) setCompanySize(data.company.company_size);
        if (data.company.description) setCompanyDescription(data.company.description);
      } else {
        throw new Error("AI не смог получить информацию о компании");
      }
    } catch (err) {
      setAiError(err.message || "Ошибка при анализе сайта");
    } finally {
      setAiLoading(false);
    }
  };

  const handleConfirmCompany = () => {
    setCompanyConfirmed(true);
  };

  const handleSkipCompany = () => {
    setSkippedCompany(true);
    setAiCompany(null);
    setCompanyName("");
    setCompanyDescription("");
    setIndustry("");
    setCompanySize("");
  };

  const handleResetCompanyLookup = () => {
    setAiCompany(null);
    setCompanyConfirmed(false);
    setSkippedCompany(false);
    setAiError("");
  };

  const handleSaveAndFinish = async () => {
    const cleanServices = freelanceServices.filter((s) => s.trim());
    const updates = {
      client_type: clientType,
      job_title: jobTitle,
      company_name: companyName,
      company_website: companyWebsite,
      company_size: companySize,
      company_description: companyDescription,
      industry: industry,
      freelance_services: cleanServices,
      client_onboarding_completed: true
    };

    if (onSave) {
      await onSave(updates);
    }
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center gap-3 flex-1">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium transition-colors ${
                step > i + 1
                  ? "bg-accent-600 text-white"
                  : step === i + 1
                  ? "bg-primary-600 text-white"
                  : "bg-neutral-800 text-neutral-500"
              }`}
            >
              {step > i + 1 ? <CheckCircle className="w-5 h-5" /> : i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`flex-1 h-0.5 rounded-full transition-colors ${
                  step > i + 1 ? "bg-accent-600" : "bg-neutral-800"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <StepSelectType
            key="step-type"
            selected={clientType}
            onSelect={handleSelectType}
          />
        )}

        {step === 2 && clientType === "hr_specialist" && (
          <StepCompanyRole
            key="step-hr"
            roleType="hr"
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            companyWebsite={companyWebsite}
            setCompanyWebsite={setCompanyWebsite}
            aiLoading={aiLoading}
            aiError={aiError}
            aiCompany={aiCompany}
            companyConfirmed={companyConfirmed}
            skippedCompany={skippedCompany}
            onAnalyze={handleAnalyzeCompany}
            onConfirm={handleConfirmCompany}
            onSkip={handleSkipCompany}
            onReset={handleResetCompanyLookup}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 2 && clientType === "business_owner" && (
          <StepCompanyRole
            key="step-biz"
            roleType="business"
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            companyWebsite={companyWebsite}
            setCompanyWebsite={setCompanyWebsite}
            aiLoading={aiLoading}
            aiError={aiError}
            aiCompany={aiCompany}
            companyConfirmed={companyConfirmed}
            skippedCompany={skippedCompany}
            onAnalyze={handleAnalyzeCompany}
            onConfirm={handleConfirmCompany}
            onSkip={handleSkipCompany}
            onReset={handleResetCompanyLookup}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 2 && clientType === "company_employee" && (
          <StepCompanyRole
            key="step-employee"
            roleType="employee"
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            companyWebsite={companyWebsite}
            setCompanyWebsite={setCompanyWebsite}
            aiLoading={aiLoading}
            aiError={aiError}
            aiCompany={aiCompany}
            companyConfirmed={companyConfirmed}
            skippedCompany={skippedCompany}
            onAnalyze={handleAnalyzeCompany}
            onConfirm={handleConfirmCompany}
            onSkip={handleSkipCompany}
            onReset={handleResetCompanyLookup}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 2 && clientType === "freelancer" && (
          <StepFreelancerDetails
            key="step-freelancer"
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            freelanceServices={freelanceServices}
            setFreelanceServices={setFreelanceServices}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <StepWelcome
            key="step-welcome"
            clientType={clientType}
            onBack={() => setStep(2)}
            onFinish={handleSaveAndFinish}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function StepSelectType({ selected, onSelect }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-600/20 flex items-center justify-center">
          <User className="w-8 h-8 text-primary-400" />
        </div>
        <h2 className="text-2xl font-medium text-white mb-2">
          Добро пожаловать на платформу!
        </h2>
        <p className="text-lg font-extralight text-neutral-400">
          Расскажите о себе, чтобы мы подстроили платформу под ваши задачи
        </p>
      </div>

      <div className="space-y-3">
        {CLIENT_TYPES.map((type) => {
          const Icon = type.icon;
          const colors = colorClasses[type.color];
          const isSelected = selected === type.id;
          return (
            <button
              key={type.id}
              onClick={() => onSelect(type.id)}
              className={`w-full p-5 rounded-md border transition-all text-left group ${
                isSelected
                  ? `${colors.activeBg} ${colors.border}`
                  : "border-neutral-700 bg-neutral-900/30 hover:bg-neutral-800/50 hover:border-neutral-600"
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-md ${colors.bg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-medium text-white mb-0.5 group-hover:text-neutral-200 transition-colors">
                    {type.title}
                  </h3>
                  <p className="text-lg font-extralight text-neutral-500 mb-1">
                    {type.subtitle}
                  </p>
                  <p className="text-lg font-extralight text-neutral-400">
                    {type.description}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-neutral-600 group-hover:text-neutral-400 transition-colors mt-1 flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

const ROLE_CONFIG = {
  hr: {
    icon: UserCheck,
    iconColor: "text-primary-400",
    iconBg: "bg-primary-600/20",
    title: "Подробности о вашей роли",
    subtitle: "Эта информация поможет нам подбирать релевантных экспертов",
    jobLabel: "Ваша должность",
    jobPlaceholder: "Например: HRD, HRBP, HR-менеджер",
    showJobTitle: true,
    btnColor: "bg-primary-600 hover:bg-primary-500"
  },
  business: {
    icon: Building2,
    iconColor: "text-accent-400",
    iconBg: "bg-accent-600/20",
    title: "Расскажите о вашем бизнесе",
    subtitle: "Чтобы эксперты и AI-напарник лучше понимали ваш контекст",
    jobLabel: null,
    jobPlaceholder: null,
    showJobTitle: false,
    btnColor: "bg-accent-600 hover:bg-accent-500"
  },
  employee: {
    icon: Briefcase,
    iconColor: "text-blue-400",
    iconBg: "bg-blue-600/20",
    title: "Расскажите о себе и компании",
    subtitle: "Контекст вашей работы помогает подобрать нужных экспертов и настроить AI-напарника",
    jobLabel: "Ваша должность",
    jobPlaceholder: "Например: Менеджер продукта, Маркетолог, Аналитик",
    showJobTitle: true,
    btnColor: "bg-primary-600 hover:bg-primary-500"
  }
};

function StepCompanyRole({
  roleType, jobTitle, setJobTitle, companyWebsite, setCompanyWebsite,
  aiLoading, aiError, aiCompany, companyConfirmed, skippedCompany,
  onAnalyze, onConfirm, onSkip, onReset, onBack, onNext
}) {
  const config = ROLE_CONFIG[roleType];
  const Icon = config.icon;

  const canProceed = companyConfirmed || skippedCompany;

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${config.iconBg} flex items-center justify-center`}>
          <Icon className={`w-8 h-8 ${config.iconColor}`} />
        </div>
        <h2 className="text-2xl font-medium text-white mb-2">
          {config.title}
        </h2>
        <p className="text-lg font-extralight text-neutral-400">
          {config.subtitle}
        </p>
      </div>

      {config.showJobTitle && (
        <div>
          <label className="block text-lg font-extralight text-neutral-300 mb-2">
            {config.jobLabel}
          </label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder={config.jobPlaceholder}
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
          />
        </div>
      )}

      {/* Company website + AI lookup */}
      {!companyConfirmed && !skippedCompany && (
        <>
          <div>
            <label className="block text-lg font-extralight text-neutral-300 mb-2">
              Сайт компании
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="url"
                  value={companyWebsite}
                  onChange={(e) => {
                    setCompanyWebsite(e.target.value);
                    if (aiCompany) onReset();
                  }}
                  placeholder="https://example.com"
                  className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && companyWebsite.trim() && !aiLoading) {
                      e.preventDefault();
                      onAnalyze();
                    }
                  }}
                />
              </div>
              <button
                onClick={onAnalyze}
                disabled={!companyWebsite.trim() || aiLoading}
                className="flex items-center gap-2 px-5 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors flex-shrink-0"
              >
                {aiLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
                {aiLoading ? "Анализ..." : "Найти"}
              </button>
            </div>
            <p className="text-lg font-extralight text-neutral-600 mt-1.5">
              AI-напарник найдёт информацию о компании для более точных рекомендаций
            </p>
          </div>

          {aiError && (
            <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-lg font-extralight text-red-300">{aiError}</p>
            </div>
          )}

          {aiCompany && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="p-5 rounded-md bg-primary-500/5 border border-primary-500/20">
                <div className="flex items-start gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-lg font-medium text-primary-300 mb-0.5">
                      AI нашёл информацию о компании
                    </p>
                    <p className="text-lg font-extralight text-primary-300/70">
                      Проверьте — это ваша компания?
                    </p>
                  </div>
                </div>

                <div className="space-y-3 ml-8">
                  <div>
                    <p className="text-lg font-extralight text-neutral-500 mb-0.5">Компания</p>
                    <p className="text-xl font-medium text-white">{aiCompany.company_name}</p>
                  </div>

                  {aiCompany.description && (
                    <div>
                      <p className="text-lg font-extralight text-neutral-500 mb-0.5">Описание</p>
                      <p className="text-lg font-extralight text-neutral-300 leading-relaxed">
                        {aiCompany.description}
                      </p>
                    </div>
                  )}

                  {aiCompany.products?.length > 0 && (
                    <div>
                      <p className="text-lg font-extralight text-neutral-500 mb-1">Продукты / услуги</p>
                      <div className="flex flex-wrap gap-2">
                        {aiCompany.products.map((p, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 rounded-md bg-neutral-800/50 text-neutral-300 text-lg font-extralight border border-neutral-700/50"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiCompany.industry && (
                    <div>
                      <p className="text-lg font-extralight text-neutral-500 mb-0.5">Отрасль</p>
                      <span className="inline-block px-3 py-1 rounded-md bg-primary-500/10 text-primary-300 text-lg font-extralight border border-primary-500/20">
                        {aiCompany.industry}
                      </span>
                    </div>
                  )}

                  {aiCompany.market && (
                    <div>
                      <p className="text-lg font-extralight text-neutral-500 mb-0.5">Рынок</p>
                      <p className="text-lg font-extralight text-neutral-400">
                        {aiCompany.market}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onConfirm}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-accent-600 hover:bg-accent-500 text-white text-lg font-medium transition-colors"
                >
                  <CheckCircle className="w-5 h-5" />
                  Да, это моя компания
                </button>
                <button
                  onClick={onSkip}
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors"
                >
                  Введу позже
                </button>
              </div>
            </motion.div>
          )}

          {!aiCompany && !aiLoading && !aiError && (
            <button
              onClick={onSkip}
              className="w-full text-center text-lg font-extralight text-neutral-500 hover:text-neutral-300 transition-colors py-2"
            >
              Пропустить — введу информацию о компании позже
            </button>
          )}
        </>
      )}

      {/* Company confirmed state */}
      {companyConfirmed && aiCompany && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-md bg-accent-500/10 border border-accent-500/20"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0" />
              <div>
                <p className="text-lg font-medium text-accent-300">
                  {aiCompany.company_name}
                </p>
                <p className="text-lg font-extralight text-accent-300/70">
                  {aiCompany.industry}
                </p>
              </div>
            </div>
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-800/50 text-lg font-extralight transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Изменить
            </button>
          </div>
        </motion.div>
      )}

      {/* Skipped state */}
      {skippedCompany && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-md bg-neutral-800/50 border border-neutral-700/50"
        >
          <div className="flex items-center justify-between">
            <p className="text-lg font-extralight text-neutral-400">
              Информация о компании не указана — можно добавить позже в настройках
            </p>
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-800/50 text-lg font-extralight transition-colors flex-shrink-0"
            >
              <RotateCcw className="w-4 h-4" />
              Указать
            </button>
          </div>
        </motion.div>
      )}

      <div className="flex justify-between gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Назад
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-md ${config.btnColor} disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors`}
        >
          Продолжить <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}

function StepFreelancerDetails({ jobTitle, setJobTitle, freelanceServices, setFreelanceServices, onBack, onNext }) {
  const addService = () => setFreelanceServices((p) => [...p, ""]);
  const removeService = (i) => setFreelanceServices((p) => p.filter((_, idx) => idx !== i));
  const updateService = (i, val) => setFreelanceServices((p) => p.map((s, idx) => (idx === i ? val : s)));

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-600/20 flex items-center justify-center">
          <Wrench className="w-8 h-8 text-green-400" />
        </div>
        <h2 className="text-2xl font-medium text-white mb-2">
          Расскажите о вашей деятельности
        </h2>
        <p className="text-lg font-extralight text-neutral-400">
          Понимание ваших услуг поможет подобрать релевантных экспертов
        </p>
      </div>

      <div>
        <label className="block text-lg font-extralight text-neutral-300 mb-2">
          Ваша специализация
        </label>
        <input
          type="text"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          placeholder="Например: Дизайнер, Копирайтер, Разработчик"
          className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-lg font-extralight text-neutral-300">
            Какие услуги вы предоставляете?
          </label>
          {freelanceServices.length < 6 && (
            <button
              onClick={addService}
              className="text-lg font-extralight text-primary-400 hover:text-primary-300 transition-colors"
            >
              + Добавить
            </button>
          )}
        </div>
        <div className="space-y-2">
          {freelanceServices.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                value={s}
                onChange={(e) => updateService(i, e.target.value)}
                placeholder={`Услуга ${i + 1}, например: Дизайн лендингов`}
                className="flex-1 px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
              {freelanceServices.length > 1 && (
                <button
                  onClick={() => removeService(i)}
                  className="px-3 py-2 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors text-lg"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Назад
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-green-600 hover:bg-green-500 text-white text-lg font-medium transition-colors"
        >
          Продолжить <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}

function StepWelcome({ clientType, onBack, onFinish }) {
  const [finishing, setFinishing] = useState(false);

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await onFinish();
    } finally {
      setFinishing(false);
    }
  };

  const scenarios = {
    hr_specialist: [
      { icon: Search, title: "Экспертные консультации", desc: "Находите топовых экспертов для обучения и развития команды" },
      { icon: Bot, title: "AI-напарник", desc: "Используйте AI для анализа HR-задач и подготовки решений" },
      { icon: Target, title: "Цикл решений", desc: "Структурируйте задачи, фиксируйте результаты, планируйте следующие шаги" }
    ],
    business_owner: [
      { icon: MessageSquare, title: "Практики из бизнеса", desc: "Обсуждайте задачи с людьми, которые уже через это прошли" },
      { icon: Bot, title: "AI-напарник", desc: "Мозговой штурм, анализ конкурентов, подготовка к переговорам — AI рядом 24/7" },
      { icon: BookOpen, title: "База знаний", desc: "Накапливайте решения, результаты и инсайты из каждой сессии" }
    ],
    company_employee: [
      { icon: Search, title: "Экспертный совет", desc: "Получите консультацию от практиков вашей отрасли" },
      { icon: Bot, title: "AI-напарник", desc: "Учитывает контекст вашей компании для точных рекомендаций" },
      { icon: Target, title: "Рост навыков", desc: "Планируйте развитие и отслеживайте прогресс" }
    ],
    freelancer: [
      { icon: Search, title: "Экспертная поддержка", desc: "Консультации по развитию вашей практики и привлечению клиентов" },
      { icon: Bot, title: "AI-напарник", desc: "Помогает анализировать рынок, готовить предложения и развивать бизнес" },
      { icon: BookOpen, title: "Структурированный рост", desc: "Фиксируйте задачи, решения и результаты в одном месте" }
    ]
  };

  const items = scenarios[clientType] || scenarios.company_employee;

  const typeLabels = {
    hr_specialist: "HR-специалист",
    business_owner: "Владелец бизнеса",
    company_employee: "Специалист компании",
    freelancer: "Фрилансер"
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-600/20 flex items-center justify-center">
          <Sparkles className="w-8 h-8 text-accent-400" />
        </div>
        <h2 className="text-2xl font-medium text-white mb-2">
          Всё готово!
        </h2>
        <p className="text-lg font-extralight text-neutral-400">
          Вот что платформа предлагает для вас как {typeLabels[clientType]?.toLowerCase() || "пользователя"}
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <div
              key={i}
              className="flex items-start gap-4 p-4 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="w-10 h-10 rounded-md bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-white mb-0.5">
                  {item.title}
                </h4>
                <p className="text-lg font-extralight text-neutral-400">
                  {item.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-4 rounded-md bg-accent-500/10 border border-accent-500/20">
        <div className="flex items-start gap-3">
          <Bot className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-lg font-medium text-accent-300 mb-1">
              AI-напарник уже ждёт вас
            </p>
            <p className="text-lg font-extralight text-accent-300/80">
              Начните с того, что опишите вашу текущую задачу или проблему — AI поможет структурировать мысли и предложит план действий
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Назад
        </button>
        <button
          onClick={handleFinish}
          disabled={finishing}
          className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 text-white text-lg font-medium transition-colors"
        >
          {finishing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Сохранение...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" /> Начать работу
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}
